/**
 * Runtime configuration helper.
 *
 * Fallback chain for each key:
 *   1. window.__RUNTIME_CONFIG__[key]  – injected at container start via public/config.js
 *   2. process.env[key]               – embedded at build time via .env
 *   3. defaultValue                   – hardcoded fallback
 */

/**
 * Returns the runtime value for the given configuration key.
 *
 * @param {string} key - The configuration key (e.g. 'REACT_APP_API_URL')
 * @param {string} [defaultValue=''] - Fallback when neither runtime nor build-time value is set
 * @returns {string} The resolved configuration value
 */
export const getRuntimeConfig = (key, defaultValue = '') => {
  const runtimeValue =
    window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__[key];

  if (runtimeValue) {
    return runtimeValue;
  }

  const buildTimeValue = process.env[key];
  if (buildTimeValue) {
    return buildTimeValue;
  }

  return defaultValue;
};

/**
 * Optional runtime settings distinguish an absent key from an explicitly
 * empty value. The empty value acts as a runtime kill switch.
 */
const getOptionalRuntimeConfig = (key, defaultValue = '') => {
  const runtimeConfig = window.__RUNTIME_CONFIG__;
  if (runtimeConfig && Object.prototype.hasOwnProperty.call(runtimeConfig, key)) {
    return runtimeConfig[key] == null ? '' : String(runtimeConfig[key]);
  }
  return getRuntimeConfig(key, defaultValue);
};

/**
 * Returns the backend API base URL.
 *
 * @returns {string} The API base URL
 */
export const getApiUrl = () =>
  getRuntimeConfig('REACT_APP_API_URL', 'http://localhost:8040/api');

/**
 * Returns the Google Play Store URL of the native Android app.
 * Empty string while the app is not yet published — the install banner
 * then falls back to the PWA install prompt.
 *
 * Only https:// URLs are accepted: the value is rendered as a link href,
 * and a manipulated/misconfigured config.js must not be able to inject
 * javascript:/data: URLs.
 *
 * @returns {string} The Play Store URL or ''
 */
export const getPlayStoreUrl = () => {
  const url = getRuntimeConfig('REACT_APP_PLAY_STORE_URL', '');
  return url.startsWith('https://') ? url : '';
};

/** Google Ads Tag-IDs haben immer die Form "AW-" gefolgt von mindestens 6 Ziffern. */
const GOOGLE_ADS_ID_PATTERN = /^AW-\d{6,}$/;

/**
 * Returns the configured Google Ads Tag-ID (e.g. "AW-18316473458").
 *
 * Nur ein syntaktisch gültiges Format wird akzeptiert: analog zur
 * https://-Prüfung in getPlayStoreUrl() darf eine manipulierte/fehlerhafte
 * config.js keinen beliebigen String in den dynamisch erzeugten
 * <script src="...">-Tag in googleAds.js einschleusen können.
 *
 * @returns {string} Die Google Ads Tag-ID oder ''
 */
export const getGoogleAdsId = () => {
  const id = getOptionalRuntimeConfig('REACT_APP_GOOGLE_ADS_ID', '').trim();
  return GOOGLE_ADS_ID_PATTERN.test(id) ? id : '';
};

/** Erlaubte Zeichen für ein Google-Ads-Conversion-Label (Base64url-ähnlich). */
const GOOGLE_ADS_CONVERSION_LABEL_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * Returns the configured Google Ads conversion label (das Label hinter dem
 * "/" in send_to, z.B. "AW-18316473458/AbCdEfGhIjKlMnOp"). Zu finden im
 * Google-Ads-Konto unter "Tools und Einstellungen" → "Conversions" →
 * gewünschte Aktion → "Tag einrichten" → "Conversion-ID und -Label".
 *
 * Leer oder ein Wert mit unzulässigen Zeichen ergibt '' — trackConversion()
 * in googleAds.js wird dann zum No-op, statt einen manipulierten Wert in
 * den send_to-Parameter zu übernehmen.
 *
 * @returns {string} Das Conversion-Label oder ''
 */
export const getGoogleAdsConversionLabel = () => {
  const label = getOptionalRuntimeConfig('REACT_APP_GOOGLE_ADS_CONVERSION_LABEL', '').trim();
  return GOOGLE_ADS_CONVERSION_LABEL_PATTERN.test(label) ? label : '';
};
